import { assert } from "@/core";
import { BaseService } from "./base.service";
import { ResponseWrapper } from "./util";

export class TimeslotService extends BaseService {
  static get entity() {
    return "timeslot";
  }

  /**
   * Get all timeslots
   * @param {Object} parameters - Query parameters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getAll(parameters = {}) {
    assert.object(parameters);

    const params = { ...parameters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}`,
      { params }
    );
    return new ResponseWrapper(response, response);
  }

  /**
   * Get timeslot by ID
   * @param {string} id - Timeslot ID
   * @returns {Promise<ResponseWrapper>}
   */
  static async getById(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).get(
      `${this.entity}/${id}`
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Create a new timeslot
   * @param {Object} data - Timeslot data
   * @returns {Promise<ResponseWrapper>}
   */
  static async create(data = {}) {
    assert.object(data, { required: true });
    assert.string(data.day, { required: true });
    assert.string(data.startTime, { required: true });
    assert.string(data.endTime, { required: true });

    const response = await this.request({ auth: true }).post(
      `${this.entity}`,
      data
    );
    return new ResponseWrapper(response, response);
  }

  /**
   * Update a timeslot
   * @param {string} id - Timeslot ID
   * @param {Object} data - Updated data
   * @returns {Promise<ResponseWrapper>}
   */
  static async update(id, data = {}) {
    assert.string(id, { required: true });
    assert.object(data, { required: true });

    const response = await this.request({ auth: true }).patch(
      `${this.entity}/${id}`,
      data
    );
    return new ResponseWrapper(response, response);
  }

  /**
   * Delete a timeslot
   * @param {string} id - Timeslot ID
   * @returns {Promise<ResponseWrapper>}
   */
  static async delete(id) {
    assert.string(id, { required: true });

    const response = await this.request({ auth: true }).delete(
      `${this.entity}/${id}`
    );
    return new ResponseWrapper(response, response);
  }

  /**
   * Get timeslots by day
   * @param {string} day - Day of week
   * @param {Object} parameters - Additional parameters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getByDay(day, parameters = {}) {
    assert.string(day, { required: true });
    assert.object(parameters);

    const params = { day, ...parameters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}/by-day`,
      { params }
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Get timeslots by appointment type
   * @param {string} appointmentTypeId - Appointment type ID
   * @param {Object} parameters - Additional parameters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getByAppointmentType(appointmentTypeId, parameters = {}) {
    assert.string(appointmentTypeId, { required: true });
    assert.object(parameters);

    const params = { appointmentTypeId, ...parameters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}/by-type`,
      { params }
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Get available timeslots
   * @param {Object} filters - Filter criteria
   * @returns {Promise<ResponseWrapper>}
   */
  static async getAvailable(filters = {}) {
    assert.object(filters);

    const params = { ...filters };

    const response = await this.request({ auth: true }).get(
      `${this.entity}/available`,
      { params }
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Bulk create timeslots
   * @param {Array} timeslots - Array of timeslot objects
   * @returns {Promise<ResponseWrapper>}
   */
  static async bulkCreate(timeslots = []) {
    assert.array(timeslots, { required: true });

    const response = await this.request({ auth: true }).post(
      `${this.entity}/bulk`,
      { timeslots }
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Duplicate timeslot
   * @param {string} id - Timeslot ID to duplicate
   * @param {Object} modifications - Optional modifications
   * @returns {Promise<ResponseWrapper>}
   */
  static async duplicate(id, modifications = {}) {
    assert.string(id, { required: true });
    assert.object(modifications);

    const response = await this.request({ auth: true }).post(
      `${this.entity}/${id}/duplicate`,
      modifications
    );
    return new ResponseWrapper(response, response.data.data);
  }

  /**
   * Get available timeslots by day
   * @param {string} day - Day of week (e.g., 'Monday')
   * @param {Object} parameters - Additional parameters
   * @returns {Promise<ResponseWrapper>}
   */
  static async getTimeslotsByDay(day, parameters = {}) {
    assert.string(day, { required: true });
    assert.object(parameters);

    const params = { day, ...parameters };

    const response = await this.request({ auth: true }).get(
      `timeslot`,
      { params }
    );
    return new ResponseWrapper(response, response.data);
  }
}